/**
 * 注册页「密码规则」失败次数与短时锁定（本地 localStorage）
 *
 * 说明：状态保存在浏览器本地，清除数据或换浏览器会重置计数。
 * 若产品要求「换浏览器也无法绕过」，必须在服务端对「用户名 + IP」等维度做限流/锁定，
 * 本模块仅承担前端提示与交互兜底，可与后端策略叠加使用。
 */

const STORAGE_KEY = 'dade_register_pwd_rule_fail_v1'
/** 连续不符合规则的提交次数达到该值即触发锁定 */
export const REGISTER_PWD_MAX_FAILS = 5
/** 锁定持续时间（毫秒）：10 分钟 */
export const REGISTER_PWD_LOCK_MS = 10 * 60 * 1000

function readAll() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		return raw ? JSON.parse(raw) : {}
	} catch (e) {
		return {}
	}
}

function writeAll(data) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
	} catch (e) {
		// 隐私模式/存储满等异常时静默失败，页面仍可走校验逻辑
	}
}

function userKey(username) {
	const u = (username || '').trim()
	return u || '__empty__'
}

/**
 * 合并「锁已过期」后的状态重置（失败次数清零，保留历史锁定次数统计）
 */
function afterLockExpired(rec) {
	return {
		fails: 0,
		lockUntil: 0,
		lifetimeLockCount: rec.lifetimeLockCount || 0
	}
}

/**
 * @param {string} username 当前注册用户名（区分大小写，须与表单一致）
 * @returns {{
 *   locked: boolean,
 *   fails: number,
 *   remainingAttempts: number,
 *   lockEndsIn: number,
 *   lockUntil: number,
 *   lifetimeLockCount: number
 * }}
 */
export function getRegisterPwdLockInfo(username) {
	const key = userKey(username)
	const all = readAll()
	let rec = all[key] || {
		fails: 0,
		lockUntil: 0,
		lifetimeLockCount: 0
	}
	const now = Date.now()

	if (rec.lockUntil && now >= rec.lockUntil) {
		rec = afterLockExpired(rec)
		all[key] = rec
		writeAll(all)
	}

	const locked = !!(rec.lockUntil && now < rec.lockUntil)
	const lockEndsIn = locked ? Math.max(0, rec.lockUntil - now) : 0
	const remainingAttempts = locked ? 0 : Math.max(0, REGISTER_PWD_MAX_FAILS - rec.fails)

	return {
		locked,
		fails: locked ? REGISTER_PWD_MAX_FAILS : rec.fails,
		remainingAttempts,
		lockEndsIn,
		lockUntil: rec.lockUntil || 0,
		lifetimeLockCount: rec.lifetimeLockCount || 0
	}
}

/**
 * 记一次「提交时密码/确认密码不符合注册规则」的失败（同一用户名维度）
 * 已在锁定期内则不会累加。
 */
export function recordRegisterPwdFailure(username) {
	const key = userKey(username)
	if (key === '__empty__') {
		return getRegisterPwdLockInfo('')
	}

	const all = readAll()
	let rec = all[key] || {
		fails: 0,
		lockUntil: 0,
		lifetimeLockCount: 0
	}
	const now = Date.now()

	if (rec.lockUntil && now >= rec.lockUntil) {
		rec = afterLockExpired(rec)
	}
	if (rec.lockUntil && now < rec.lockUntil) {
		all[key] = rec
		writeAll(all)
		return getRegisterPwdLockInfo(username)
	}

	rec.fails += 1
	if (rec.fails >= REGISTER_PWD_MAX_FAILS) {
		rec.lockUntil = now + REGISTER_PWD_LOCK_MS
		rec.lifetimeLockCount = (rec.lifetimeLockCount || 0) + 1
	}

	all[key] = rec
	writeAll(all)
	return getRegisterPwdLockInfo(username)
}

/** 注册成功（接口 200）后清除该用户名的本地锁定记录 */
export function clearRegisterPwdFailures(username) {
	const key = userKey(username)
	if (key === '__empty__') return
	const all = readAll()
	if (all[key]) {
		delete all[key]
		writeAll(all)
	}
}
